import { useState, useEffect, useCallback } from 'react';

export const useAudioVisualizer = (
  isActive: boolean,
  audioLevel: number = 0,
  barCount: number = 5
) => {
  const [bars, setBars] = useState<number[]>(new Array(barCount).fill(1));

  const safeRandom = () => {
    const c = globalThis.crypto as Crypto | undefined;
    if (c && typeof c.getRandomValues === 'function') {
      const arr = new Uint32Array(1);
      c.getRandomValues(arr);
      return arr[0] / 4294967296;
    }
    return Math.random(); // NOSONAR - UI-only animation variability, not security-sensitive
  };

  const updateBars = useCallback(() => {
    if (isActive) {
      setBars(prev => 
        prev.map((prevHeight) => {
          // Add randomness to create more natural movement
          const randomFactor = 0.3;
          const randomVariation = (safeRandom() * 2 - 1) * randomFactor;
          
          // Smooth transition between heights
          const targetHeight = 1 + (audioLevel * 4); // Scale to 1-5 range
          const smoothingFactor = 0.3; // Adjust for smoother/faster transitions
          const newHeight = prevHeight + (targetHeight - prevHeight) * smoothingFactor + randomVariation;
          
          // Ensure height stays within bounds
          return Math.max(1, Math.min(5, newHeight));
        })
      );
    } else {
      setBars(new Array(barCount).fill(1));
    }
  }, [isActive, audioLevel, barCount]);

  useEffect(() => {
    let animationFrame: number;
    if (isActive) {
      const animate = () => {
        updateBars();
        animationFrame = requestAnimationFrame(animate);
      };
      animate();
    }
    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, [isActive, updateBars]);

  return { bars };
};